import { PkIdentifier, PkJersey } from '@uefadigital/panenka-react';
import { memo, useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import type { FieldSizeData } from './types';
import { displayPlayerName, getPlayerSubstitutions, getPlayerCards, getPlayerGoals, playerPosition } from './utils';

import type { LineUpPlayer, MatchEvents } from 'types/ApiResponseInterfaces';
import type { ConfigCompetition } from 'utils/config';
import { getPlayerLink, handleGoToLink } from 'utils/helpers';

import * as style from './style.module.css';

const LineupPlayer = ({
  player,
  isHome,
  color,
  competition,
  isDesktop,
  fieldSize,
  mainEvents,
}: {
  player: LineUpPlayer;
  isHome: boolean;
  color: string;
  competition: ConfigCompetition | undefined;
  isDesktop: boolean;
  fieldSize: FieldSizeData;
  mainEvents: Array<MatchEvents>;
}) => {
  const { t } = useTranslation();
  const isCaptain = useMemo(() => ['CAPTAIN', 'GOALKEEPER_CAPTAIN'].includes(player.type), [player.type]);
  const isGoalkeeper = useMemo(() => ['GOALKEEPER', 'GOALKEEPER_CAPTAIN'].includes(player.type), [player.type]);

  const playerName = useMemo(() => {
    let result = displayPlayerName(player);
    if (isGoalkeeper) result += ` (${t('gk')})`;
    return result;
  }, [isGoalkeeper, player, t]);

  const substitutions = getPlayerSubstitutions(player.player, mainEvents, false);
  const cards = getPlayerCards(player.player, mainEvents);
  const goals = getPlayerGoals(player.player, mainEvents).goalsNumber;
  const ownGoals = getPlayerGoals(player.player, mainEvents).ownGoalsNumber;

  const playerLink = competition && getPlayerLink(player.player.id, competition);

  return (
    <Fragment>
      <PkIdentifier
        type="vertical"
        className="pk-identifier pk-position--absolute"
        style={{
          zIndex: 1,
          top: playerPosition(player, isHome)?.top,
          left: playerPosition(player, isHome)?.left,
          transform: 'translate(-50%, -50%)',
          padding: '0 10px',
        }}
      >
        <span
          onClick={() => handleGoToLink(playerLink)}
          slot="prefix"
          className={`${playerLink ? style.pointer : ''} identifier-prefix pk-p--0 pk-m--0 pr-mb--0`}
        >
          <PkJersey
            size={isDesktop ? fieldSize.desktopJerseySize : fieldSize.mobileJerseySize}
            number={player.jerseyNumber}
            shirtColor={color}
            substitution={substitutions}
            goals={goals}
            ownGoals={ownGoals}
            cards={cards}
            captainLabel={isCaptain ? 'c' : undefined}
          />
        </span>
        <span
          onClick={() => handleGoToLink(playerLink)}
          slot="primary"
          className={`${
            playerLink ? style.pointer : ''
          } pk-font-size--xs pk-line-height--sm pk-text--elevation-01 pr-font-size--10`}
        >
          {playerName}
        </span>
      </PkIdentifier>
    </Fragment>
  );
};

export default memo(LineupPlayer);
